<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.terminations_by_myenv')" active></b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <div class="metric-badge">
            <span class="metric-label">{{ $t("globals.pending") }}</span>
            <span class="metric-value">{{ getTerminationsTotal }}</span>
          </div>
          <div class="metric-badge">
            <span class="metric-label">{{ $t("globals.this_week") }}</span>
            <span class="metric-value">{{ getWeekTotal }}</span>
          </div>
        </div>
      </div>
      <div class="tab-content-inner">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="search-container">
            <label class="search-label">{{ $t("globals.search") }}</label>
            <b-form-input
              v-model="filter"
              @change="onFilterChange"
              class="search-input"
              :placeholder="$t('globals.search') + '...'"
              :disabled="getTerminationsLoading"
            />
          </div>

          <div class="filter-group">
            <div class="filter-item">
              <label class="filter-label">{{ $t("globals.status") }}</label>
              <b-form-select
                v-model="searchQuery.status"
                @change="onStatusChange"
                :options="statusOptions"
                class="filter-select"
              />
            </div>
          </div>

          <div class="action-buttons">
            <button
              class="reload-btn"
              @click="fetchData"
              :disabled="getTerminationsLoading"
              v-b-tooltip.hover
              :title="$t('globals.reload')"
            >
              <icon name="sync-alt" :spin="getTerminationsLoading"></icon>
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrapper" style="position: relative; min-height: 300px;">
          <!-- Loading Overlay -->
          <div v-if="getTerminationsLoading" class="loading-overlay">
            <div class="spinner"></div>
            <p>{{ $t("globals.loading") }}...</p>
          </div>

          <!-- Table Content -->
          <table class="modern-table">
            <thead>
              <tr>
                <th
                  v-for="field in fields"
                  :key="field.key"
                  :class="{ sortable: field.sortable, [field.class]: field.class }"
                  @click="field.sortable && handleSort(field.key)"
                >
                  {{ field.label || field.key }}
                  <span v-if="sortBy === field.key" class="sort-icon">
                    {{ sortDesc ? '▼' : '▲' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- Empty State Row -->
              <tr v-if="!getTerminationsLoading && getTerminations.length === 0">
                <td :colspan="fields.length" class="empty-state-cell">
                  {{ $t("globals.no_results") }}
                </td>
              </tr>

              <!-- Data Rows -->
              <template v-for="item in getTerminations">
                <tr
                  :key="`termination-row-${item.id}`"
                  class="table-row termination-row"
                  :class="{ 'is-selected': expandedTerminationId === item.id }"
                  
                >
                  <td class="id-click-cell" @click="toggleNotesRow(item.id)">{{ item.id }}</td>
                  <td>
                    <span class="status-badge" :class="`status-${item.status.toLowerCase()}`">
                      {{ item.status }}
                    </span>
                  </td>
                  <td>
                      <router-link class="plain-pointer-link" :to="{ name: 'device', params: { id: item.device_id } }">
                        {{ item.imei }}
                      </router-link>
                  </td>
                  <td>
                    <router-link class="plain-pointer-link" :to="{ name: 'customer', params: { id: item.customer_id } }">
                      {{ item.customer }}
                    </router-link>
                  </td>
                  <td>
                    <span v-if="item.payments_up_to_date" class="status-badge ok" > {{ $t("globals.yes") }} </span>
                    <span v-else class="status-badge fail"> {{ $t("globals.no") }} </span>
                  </td>
                  <td>
                    <span v-if="item.terminate_at" class="status-badge" :class="['item', item.can_process ? 'ok' : 'fail']">
                      {{ formatDate(item.terminate_at) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    {{ item.return_process || '-' }}
                    
                    <router-link
                      v-if="item.return_process == 'lost_device' && item.order && item.order.invoices.length > 0 "
                      class="plain-pointer-link"
                      :to="{ name: 'order', params: { id: item.order.id } }"
                    > 
                      <span 
                        :class="['status-badge', allInvoicesPaid(item.invoices) ? 'ok' : 'fail']" :title="allInvoicesPaid(item.invoices) ?  $t('globals.paid') : $t('globals.not_paid')">
                        <icon name="file-invoice"></icon>
                      </span>
                    </router-link>

                    <a v-if="item.return_process == 'deinstallation' && item.removal_installation_id"
                      href="#"
                      class="removal-form-link"
                      :title="$t('globals.view_form')"
                      @click.stop.prevent="goToRemovalForm(item.removal_installation_id)"
                    >
                      <icon name="file-alt"></icon>
                    </a>
                  </td>
                  <td>
                    <span
                      v-if="item.return_process === 'deinstallation'"
                      :class="['status-badge', item.removal_installation_id ? 'ok' : 'fail']"
                    >
                      {{ item.removal_installation_id ? $t('globals.yes') : $t('globals.no') }}
                    </span>

                    <span v-else>-</span>
                  </td>
                  <td>
                    <span v-if="item.has_pending_services" class="status-badge fail">
                      {{ $t("globals.yes") }}
                    </span>
                    <span v-else class="status-badge ok">
                      {{ $t("globals.no") }}
                    </span>
                  </td>
                  <td>
                    <span v-if="item.last_device" class="status-badge ok">
                      {{ $t("globals.yes") }}
                    </span>
                    <span v-else class="status-badge fail">
                      {{ $t("globals.no") }}
                    </span>

                    <span v-if="item.last_device && item.has_not_paid_invoices" class="status-badge fail" style="margin-left: 4px;">
                      {{ $t("globals.has_not_paid_invoices") }}
                    </span>
                  </td>
                  <td>
                    {{ item.created_at ? formatDate(item.created_at) : '-' }}
                  </td>
                </tr>
                <tr
                  v-if="expandedTerminationId === item.id"
                  :key="`termination-notes-${item.id}`"
                  class="notes-row"
                >
                  <td :colspan="fields.length" class="notes-cell">
                    <strong>{{ $t("globals.notes") }}:</strong>
                    {{ item.notes || '-' }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-section">
          <div class="controls-left">
            <span class="summary-label">
              {{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + perPage, getTerminationsTotal) }}
            </span>
            <select v-model="perPage" class="per-page-select">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="40">40</option>
              <option :value="60">60</option>
            </select>
          </div>
          <div class="pagination-controls">
            <button
              class="page-btn"
              :disabled="offset === 0 || getTerminationsLoading"
              @click="fetchLess"
            >
              &lt;
            </button>
            <button
              class="page-btn"
              :disabled="getTerminations.length < perPage || getTerminationsLoading"
              @click="fetchMore"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import "vue-awesome/icons/spinner";
import "vue-awesome/icons/sync-alt";
import "vue-awesome/icons/file-alt";
import "vue-awesome/icons/file-invoice";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      filter: null,
      offset: 0,
      perPage: 20,
      expandedTerminationId: null,
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        { key: "id", label: "ID", sortable: false },
        { key: "status", label: this.$t("globals.status"), sortable: true },
        { key: "imei", label: "IMEI", sortable: true },
        { key: "customer", label: this.$t("globals.customer"), sortable: false },
        { key: 'payments_up_to_date', label: this.$t("globals.payments_up_to_date"), sortable: false },
        { key: "terminate_at", label: this.$t("globals.terminate_at"), sortable: true },
        { key: "return_process", label: this.$t("globals.return_process"), sortable: true },
        { key: "removal_installation_id", label: this.$t("globals.removed"), sortable: false },
        { key: "has_pending_services", label: this.$t("globals.pending_services"), sortable: false },
        {key: 'last_device', label: this.$t("globals.last_device"), sortable: false},
        { key: "created_at", label: this.$t("globals.created_at"), sortable: true },
      ],
      searchQuery: {
        status: 'pending',
      },
      statusOptions: [
        { value: "all", text: this.$t("globals.all") },
        { value: "pending", text: this.$t("globals.pending") },
        { value: "completed", text: this.$t("globals.completed") },
        { value: "canceled", text: this.$t("globals.canceled") },
        { value: "expired", text: this.$t("globals.expired") },
      ],
    };
  },
  computed: {
    ...mapGetters("terminations", [
      "getTerminations",
      "getTerminationsLoading",
      "getTerminationsTotal",
      "getWeekTotal",
    ]),
    ...mapGetters(["getWebAppUrl"]),
  },
  methods: {
    ...mapActions("terminations", ["fetchTerminations"]),

    onFilterChange() {
      this.offset = 0;
      this.fetchData();
    },

    onStatusChange() {
      this.offset = 0;
      this.fetchData();
    },
    
    refreshData() {
      this.offset = 0;
      this.searchQuery.status = 'pending';
      this.filter = null;
      this.fetchData();
    },

    fetchData() {
      this.fetchTerminations({
        limit: this.perPage,
        offset: this.offset,
        order_by: this.sortBy,
        order_direction: this.sortDesc ? "DESC" : "ASC",
        status: this.searchQuery.status,
        search: this.filter,
      });
    },

    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
      this.fetchData();
    },

    fetchMore() {
      this.offset += this.perPage;
      this.fetchData();
    },

    fetchLess() {
      if (this.offset >= this.perPage) {
        this.offset -= this.perPage;
        this.fetchData();
      }
    },

    goToRemovalForm(id) {
      window.open(
        `${this.getWebAppUrl}/admin/device_installations/${id}`,
        "_blank"
      );
    },

    toggleNotesRow(terminationId) {
      this.expandedTerminationId =
        this.expandedTerminationId === terminationId ? null : terminationId;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";

      if (dateStr.includes("/")) {
        const [day, month, year] = dateStr.split("/");
        return `${day}-${month}-${year}`;
      }

      const date = new Date(dateStr);
      if (isNaN(date)) return "";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    allInvoicesPaid(invoices){
      if (!invoices || invoices.length === 0) return false;
      return invoices.every(invoice => invoice.paid_at !== null);
    }
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    perPage() {
      this.offset = 0; // reset offset when perPage changes
      this.fetchData();
    },
  },
};
</script>

<style scoped>

.modern-table tbody tr.termination-row {
  cursor: default;
}

.modern-table tbody tr.termination-row:hover {
  cursor: default;
}

.id-click-cell {
  cursor: pointer;
}

.plain-pointer-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.plain-pointer-link:hover,
.plain-pointer-link:focus {
  color: #0d6efd;
  text-decoration: underline;
}

.termination-row:hover td {
  background-color: #ecedf1;
}

.termination-row.is-selected td {
  background-color: #e3f2fd;
}

.notes-row {
  background-color: #f5f6fa;
}

.notes-cell {
  padding: 12px 16px;
  color: #2d3748;
  white-space: pre-wrap;
}

.removal-form-link {
  margin-left: 8px;
  color: #17a2b8;
}
</style>